import { nonNullishOrThrow } from '@web-platform/shared-universal-utils-frontend/src/helpers/assertNonNullish';

import type {
  SkyscannerSonarCommonV1Enums,
  SkyscannerSonarV3Response,
} from '@skyscanner-internal/sonar-web-http-client';
import type { AttributeType } from '@web-platform/shared-universal-constants/src/filterAttributes';
import type { AttributeOptionIdLookup } from '@web-platform/shared-universal-types/src/redux/state/entities/flights/itineraries';

type Attribute = SkyscannerSonarCommonV1Enums.Attribute;
type SonarPricingOptionIdsByAttribute = SkyscannerSonarV3Response.PricingOptionIdsByAttribute;

const ATTRIBUTE_TYPE_MAP = {
  ATTRIBUTE_UNSPECIFIED: 'UNSPECIFIED',
  ATTRIBUTE_CHECKED_BAGGAGE: 'CHECKED_BAGGAGE',
  ATTRIBUTE_CABIN_BAGGAGE: 'CABIN_BAGGAGE',
} as const;

const mapAttributeTypes = (attribute: Attribute | undefined): AttributeType | undefined =>
  attribute && ATTRIBUTE_TYPE_MAP[attribute];

export const mapAttributeIdLookup = (
  attributeIdLookup: SonarPricingOptionIdsByAttribute[],
): AttributeOptionIdLookup[] =>
  attributeIdLookup.map((idLookup) => ({
    attributes: nonNullishOrThrow(
      idLookup.attributes,
      'attributes array in pricing option ID lookup',
    ).map((attribute) =>
      nonNullishOrThrow(
        mapAttributeTypes(attribute),
        `unable to map Sonar attribute type: ${attribute}`,
      ),
    ),
    optionIds: nonNullishOrThrow(idLookup.pricingOptionIds, 'idLookup.pricingOptionIds'),
    // Map byAgent from pricingOptionIdsByAgent if available
    byAgent:
      idLookup.pricingOptionIdsByAgent?.flatMap((agentLookup) => {
        const agentIds = nonNullishOrThrow(agentLookup.agentIds, 'agentLookup.agentIds');
        const optionIds = nonNullishOrThrow(
          agentLookup.pricingOptionIds,
          'agentLookup.pricingOptionIds',
        );

        // Create an entry for each agent ID with the same option IDs
        return agentIds.map((agentId) => ({
          agentId: nonNullishOrThrow(agentId, 'agent ID from agentIds array'),
          optionIds,
        }));
      }) || [],
  }));
